import { GITHUB_REPO_URL, PURCHASE_PATH } from "../lib/site.ts";

type Plan = {
  name: string;
  content: string;
  price: string;
  suffix?: string;
  features: string[];
  cta: { label: string; href: string; external?: boolean };
  highlighted?: boolean;
};

const plans: Plan[] = [
  {
    name: "源码自构建",
    content: "源码按 Apache-2.0 开放，可自行修改授权、公钥、更新源和品牌入口",
    price: "¥0",
    features: ["可查看、修改和自构建", "可自行替换授权和更新配置", "不应冒充官方发行版"],
    cta: { label: "查看源码", href: GITHUB_REPO_URL, external: true },
  },
  {
    name: "官方年度授权",
    content: "适合先使用官方发行版一年，激活后授权校验和日常提醒均可离线完成",
    price: "¥9.9",
    suffix: "/年",
    features: ["单设备授权 1 年", "官方发行版自动更新", "既有测试终身授权继续兼容"],
    cta: { label: "购买年度授权", href: `${PURCHASE_PATH}?plan=annual` },
    highlighted: true,
  },
  {
    name: "官方终身授权",
    content: "适合同一台常用电脑长期使用，一次购买长期有效",
    price: "¥29.9",
    features: ["单设备长期授权", "官方发行版自动更新", "激活后完全离线可用"],
    cta: { label: "购买终身授权", href: `${PURCHASE_PATH}?plan=lifetime` },
  },
];

export function Pricing() {
  return (
    <section id="pricing" className="relative scroll-mt-24 px-6 py-24 sm:py-32">
      <div
        data-reveal=""
        style={{ ["--reveal-delay" as string]: "0ms" }}
        className="mx-auto max-w-3xl text-center"
      >
        <h2 className="text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
          源码开放，官方发行版保留付费授权
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
          自构建完全免费；官方安装包继续提供年度与终身授权，用于支持官方构建、自动更新和既有授权兼容。
        </p>
      </div>

      <div className="mx-auto mt-14 grid w-full max-w-6xl gap-4 md:grid-cols-3">
        {plans.map((plan, i) => (
          <div
            key={plan.name}
            data-reveal=""
            style={{ ["--reveal-delay" as string]: `${i * 80}ms` }}
            className={`flex flex-col rounded-2xl border p-6 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] transition-colors ${
              plan.highlighted
                ? "border-white/20 bg-[oklch(0.19_0.005_285_/_0.96)]"
                : "border-white/[0.08] bg-[oklch(0.17_0.005_285_/_0.92)] hover:bg-[oklch(0.19_0.005_285_/_0.94)]"
            }`}
          >
            <h3 className="text-xl font-semibold tracking-tight">{plan.name}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{plan.content}</p>
            <div className="mt-6 flex items-baseline gap-1">
              <span className="text-4xl font-semibold tracking-tight">{plan.price}</span>
              {plan.suffix ? (
                <span className="text-sm text-muted-foreground">{plan.suffix}</span>
              ) : null}
            </div>
            <ul className="mt-6 flex flex-1 flex-col gap-2.5 text-sm text-muted-foreground">
              {plan.features.map((feature) => (
                <li key={feature} className="flex gap-2">
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-foreground/40" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <a
              href={plan.cta.href}
              target={plan.cta.external ? "_blank" : undefined}
              rel={plan.cta.external ? "noopener noreferrer" : undefined}
              className={`mt-8 inline-flex h-11 items-center justify-center rounded-full text-sm font-medium transition-transform hover:-translate-y-0.5 active:translate-y-0 ${
                plan.highlighted
                  ? "bg-primary text-primary-foreground shadow-lg shadow-black/20"
                  : "border border-white/15 bg-background/25 text-foreground/90"
              }`}
            >
              {plan.cta.label}
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}
